/* ================================================================
   Zusammenführen doppelter Objekte.

   Liegt ein Wein, Winzer oder eine Region zweimal leicht
   unterschiedlich vor, wird die Quelle in das Ziel überführt. Alle
   Verweise wandern mit — sonst hingen Kritiken, Entnahmen,
   Bewegungen oder Weinkarteneinträge in der Luft.

   Grundsätze:
   - Bestände gehen nie verloren: gleiche Jahrgänge werden addiert.
   - Persönliche Angaben des Ziels haben Vorrang; leere Felder werden
     aus der Quelle gefüllt.
   - Das Ergebnis ist eine reine Funktion: nichts wird verändert,
     sondern ein neues Kellerbuch zurückgegeben.
   ================================================================ */

import aehnlichkeit from "../../aehnlichkeit.js";

const { baueIndex, suche } = aehnlichkeit;

const LISTE = { wein: "weine", winzer: "winzer", region: "regions" };

/* Leeres Feld = null, undefined, "" oder leeres Array. */
const leer = (v) => v === null || v === undefined || v === ""
  || (Array.isArray(v) && v.length === 0);

/* Ziel gewinnt; wo das Ziel nichts hat, füllt die Quelle auf. */
function felderZusammen(ziel, quelle, ausnahmen = []) {
  const out = { ...ziel };
  for (const k of Object.keys(quelle)) {
    if (ausnahmen.includes(k)) continue;
    if (leer(out[k]) && !leer(quelle[k])) out[k] = quelle[k];
  }
  return out;
}

/* Bestände zweier Jahrgänge addieren. */
function bestaendeAddieren(a = {}, b = {}) {
  const out = { ...a };
  for (const [groesse, anzahl] of Object.entries(b || {})) {
    out[groesse] = (Number(out[groesse]) || 0) + (Number(anzahl) || 0);
  }
  return out;
}

/* ----------------------------------------------------------------
   Weine: Jahrgänge nach Jahr zusammenlegen. Gleiche Jahre werden
   verschmolzen (Bestände addiert), unbekannte Jahre übernommen.
   Liefert zusätzlich die Umschlüsselung alter Jahrgangs-IDs.
   ---------------------------------------------------------------- */
function jahrgaengeZusammen(zielWein, quellWein) {
  const jahrgaenge = (zielWein.jahrgaenge || []).map((j) => ({ ...j }));
  const jgKarte = new Map();     /* alte jgId → neue jgId */

  for (const qj of quellWein.jahrgaenge || []) {
    const treffer = jahrgaenge.find((zj) => zj.jahr === qj.jahr);
    if (treffer) {
      treffer.bestaende = bestaendeAddieren(treffer.bestaende, qj.bestaende);
      if (leer(treffer.lagerort)) treffer.lagerort = qj.lagerort || "";
      if (treffer.preis == null) treffer.preis = qj.preis != null ? qj.preis : null;
      if (treffer.trinkVon == null) treffer.trinkVon = qj.trinkVon != null ? qj.trinkVon : null;
      if (treffer.trinkBis == null) treffer.trinkBis = qj.trinkBis != null ? qj.trinkBis : null;
      jgKarte.set(qj.id, treffer.id);
    } else {
      jahrgaenge.push({ ...qj });
      jgKarte.set(qj.id, qj.id);
    }
  }
  jahrgaenge.sort((a, b) => (b.jahr || 0) - (a.jahr || 0));
  return { jahrgaenge, jgKarte };
}

/* ----------------------------------------------------------------
   Hauptfunktion.
   ---------------------------------------------------------------- */
function fuehreZusammen(db, typ, quelleId, zielId) {
  if (!LISTE[typ] || quelleId === zielId) return { db, bericht: null };
  const feld = LISTE[typ];
  const quelle = (db[feld] || []).find((o) => o.id === quelleId);
  const ziel = (db[feld] || []).find((o) => o.id === zielId);
  if (!quelle || !ziel) return { db, bericht: null };

  const bericht = { typ, quelle: quelle.name, ziel: ziel.name, verweise: 0, jahrgaenge: 0 };
  let neu = { ...db };

  if (typ === "region") {
    const vereint = felderZusammen(ziel, quelle, ["id", "herkunft", "abgekoppelt"]);
    neu.regions = neu.regions.map((r) => r.id === zielId ? vereint : r).filter((r) => r.id !== quelleId);
    neu.winzer = (neu.winzer || []).map((w) => {
      if (w.regionId !== quelleId) return w;
      bericht.verweise++;
      return { ...w, regionId: zielId };
    });
    neu.weine = (neu.weine || []).map((w) => {
      if (w.regionId !== quelleId) return w;
      bericht.verweise++;
      return { ...w, regionId: zielId };
    });
    return { db: neu, bericht };
  }

  if (typ === "winzer") {
    const vereint = felderZusammen(ziel, quelle, ["id", "herkunft", "abgekoppelt"]);
    neu.winzer = neu.winzer.map((w) => w.id === zielId ? vereint : w).filter((w) => w.id !== quelleId);
    neu.weine = (neu.weine || []).map((w) => {
      if (w.winzerId !== quelleId) return w;
      bericht.verweise++;
      return { ...w, winzerId: zielId };
    });
    return { db: neu, bericht };
  }

  /* --- Wein --- */
  const { jahrgaenge, jgKarte } = jahrgaengeZusammen(ziel, quelle);
  bericht.jahrgaenge = jgKarte.size;
  const vereint = {
    ...felderZusammen(ziel, quelle, ["id", "herkunft", "abgekoppelt", "jahrgaenge"]),
    jahrgaenge,
  };
  neu.weine = neu.weine.map((w) => w.id === zielId ? vereint : w).filter((w) => w.id !== quelleId);

  /* Verweise umschlüsseln: Wein-ID und, wo vorhanden, Jahrgangs-ID. */
  const umhaengen = (eintraege) => (eintraege || []).map((e) => {
    if (e.weinId !== quelleId) return e;
    bericht.verweise++;
    const jgId = e.jgId && jgKarte.has(e.jgId) ? jgKarte.get(e.jgId) : e.jgId;
    return { ...e, weinId: zielId, jgId };
  });

  neu.kritiken = umhaengen(neu.kritiken);
  neu.entnahmen = umhaengen(neu.entnahmen);
  neu.bewegungen = umhaengen(neu.bewegungen);
  neu.weinkarten = (neu.weinkarten || []).map((k) => ({
    ...k,
    eintraege: (k.eintraege || []).map((e) => {
      if (e.weinId !== quelleId) return e;
      bericht.verweise++;
      const jgId = e.jgId && jgKarte.has(e.jgId) ? jgKarte.get(e.jgId) : e.jgId;
      return { ...e, weinId: zielId, jgId };
    }),
  }));

  return { db: neu, bericht };
}

/* ================================================================
   Was unterscheidet die beiden?

   Vor dem Zusammenführen muss sichtbar sein, was dabei auf dem Spiel
   steht. Drei Fälle je Feld:

   - `gleich`     beide sagen dasselbe (oder beide schweigen)
   - `ergaenzt`   nur eine Seite hat einen Wert — er wird übernommen,
                  egal in welche Richtung zusammengeführt wird
   - `konflikt`   beide haben einen Wert, und sie widersprechen sich.
                  Nur hier entscheidet die Richtung, und nur hier geht
                  etwas verloren.

   Gibt es keinen einzigen Konflikt, ist das Zusammenführen
   verlustfrei: Das Ergebnis trägt alle Angaben beider Seiten. Genau
   das darf die Oberfläche dann auch versprechen — und deshalb zählen
   bei Weinen die Jahrgänge mit. Ein Versprechen, das an einem
   Lagerort oder Preis im Jahrgang stillschweigend bricht, wäre
   schlimmer als gar keines.
   ================================================================ */

/* Feld, Beschriftung und — wo nötig — wie der Wert zu lesen ist.
   Die Aufbereitung selbst passiert in der Oberfläche, hier bleiben
   die Rohwerte. */
const VERGLEICHSFELDER = {
  wein: [
    ["name", "Name", "text"],
    ["winzerId", "Winzer", "winzer"],
    ["regionId", "Region", "region"],
    ["typ", "Typ", "typ"],
    ["rebsorten", "Rebsorten", "rebsorten"],
    ["appellation", "Appellation", "text"],
    ["klassifikation", "Klassifikation", "text"],
    ["boden", "Boden", "text"],
    ["label", "Eigenes Label", "text"],
  ],
  winzer: [
    ["name", "Name", "text"],
    ["ort", "Ort", "text"],
    ["regionId", "Region", "region"],
  ],
  region: [
    ["name", "Name", "text"],
    ["land", "Land", "land"],
    ["lat", "Breite", "zahl"],
    ["lon", "Länge", "zahl"],
  ],
};

/* Felder eines Jahrgangs, die beim Verschmelzen gleicher Jahre
   überschrieben werden könnten (Bestände nicht: die werden addiert). */
const JAHRGANG_FELDER = [
  ["lagerort", "Lagerort"], ["preis", "Preis"],
  ["trinkVon", "Trinkfenster ab"], ["trinkBis", "Trinkfenster bis"],
];

/* Zwei Werte auf Gleichheit prüfen — auch strukturierte wie
   Rebsortenlisten, die als Objekte vorliegen. */
function gleichwertig(a, b) {
  if (leer(a) && leer(b)) return true;
  if (leer(a) || leer(b)) return false;
  if (typeof a === "object" || typeof b === "object") {
    try { return JSON.stringify(a) === JSON.stringify(b); } catch (e) { return false; }
  }
  return String(a).trim() === String(b).trim();
}

const feldStatus = (q, z) => {
  if (gleichwertig(q, z)) return "gleich";
  if (leer(q) || leer(z)) return "ergaenzt";
  return "konflikt";
};


/** Feldweiser Vergleich zweier gleichartiger Objekte. */
function vergleicheObjekte(typ, quelle, ziel) {
  const felder = VERGLEICHSFELDER[typ] || [];
  if (!quelle || !ziel) return [];
  return felder.map(([feld, label, art]) => ({
    feld, label, art,
    quelle: quelle[feld], ziel: ziel[feld],
    status: feldStatus(quelle[feld], ziel[feld]),
  }));
}


/** Jahrgänge, die beim Zusammenführen aufeinandertreffen. Nur
    gleiche Jahre können sich widersprechen; neue Jahre wandern
    unverändert hinüber. */
function vergleicheJahrgaenge(quellWein, zielWein) {
  if (!quellWein || !zielWein) return [];
  const zeilen = [];
  for (const qj of quellWein.jahrgaenge || []) {
    const zj = (zielWein.jahrgaenge || []).find((x) => x.jahr === qj.jahr);
    if (!zj) continue;
    for (const [feld, label] of JAHRGANG_FELDER) {
      const status = feldStatus(qj[feld], zj[feld]);
      if (status === "gleich") continue;
      zeilen.push({ jahr: qj.jahr, feld, label, quelle: qj[feld], ziel: zj[feld], status });
    }
  }
  return zeilen;
}


/** Zusammenfassung für die Vorschau: Was ist gleich, was wird
    ergänzt, was widerspricht sich? */
function unterschiede(db, typ, quelleId, zielId) {
  const feld = LISTE[typ];
  if (!feld) return null;
  const quelle = (db[feld] || []).find((o) => o.id === quelleId);
  const ziel = (db[feld] || []).find((o) => o.id === zielId);
  if (!quelle || !ziel) return null;

  const felder = vergleicheObjekte(typ, quelle, ziel);
  const jahrgaenge = typ === "wein" ? vergleicheJahrgaenge(quelle, ziel) : [];
  const konflikte = [
    ...felder.filter((z) => z.status === "konflikt"),
    ...jahrgaenge.filter((z) => z.status === "konflikt"),
  ];
  return {
    quelle, ziel, felder, jahrgaenge,
    konflikte,
    ergaenzt: [...felder, ...jahrgaenge].filter((z) => z.status === "ergaenzt"),
    verlustfrei: konflikte.length === 0,
  };
}


/* ----------------------------------------------------------------
   Dublettenvorschläge im eigenen Kellerbuch.

   Früher: gleicher Namensschlüssel → eine Gruppe. Dreißig Weine
   „Riesling trocken“ von dreißig Winzern standen so als eine
   Dublettengruppe da. Jetzt dieselbe Regel wie beim Katalogabgleich
   (aehnlichkeit.js): Winzer, Typ, Land, Prädikat und Lage müssen
   zusammenpassen. Paare werden zu Gruppen verbunden.
   ---------------------------------------------------------------- */
function dublettenVorschlaege(db, typ) {
  const feld = LISTE[typ];
  if (!feld) return [];
  const index = baueIndex(typ, db);
  const eltern = new Map();
  const wurzel = (id) => { while (eltern.get(id) !== id) id = eltern.get(id); return id; };
  for (const e of index.eintraege) eltern.set(e.o.id, e.o.id);
  for (const e of index.eintraege) {
    for (const treffer of suche(index, e.m, { ausserId: e.o.id })) {
      const a = wurzel(e.o.id), b = wurzel(treffer.objekt.id);
      if (a !== b) eltern.set(a, b);
    }
  }
  const gruppen = new Map();
  for (const e of index.eintraege) {
    const w = wurzel(e.o.id);
    if (!gruppen.has(w)) gruppen.set(w, []);
    gruppen.get(w).push(e.o);
  }
  return [...gruppen.values()]
    .filter((gruppe) => gruppe.length > 1)
    .map((gruppe) => gruppe.sort((a, b) => String(a.name).localeCompare(String(b.name), "de")));
}

export { JAHRGANG_FELDER, VERGLEICHSFELDER, dublettenVorschlaege, fuehreZusammen, unterschiede, vergleicheJahrgaenge, vergleicheObjekte };
